/** Generic V1/V2 workflow progress renderer. */
import type { Job, JobStatus } from '../lib/schemas'

type SetupStage = NonNullable<Job['setup_stage']>
type V1Entry = {
  key: string
  current: number
  total: number
  label?: string | null
}
type V1Progress = { v: number; entries: V1Entry[] }
type Projection = {
  fractionMillionths?: number | null
  completed?: number | null
  total?: number | null
  unit?: string | null
  accuracy?: 'exact' | 'estimated' | null
}
type V2Node = {
  id: string
  parentId?: string | null
  order: number
  label: string
  state: string
  activity?: string | null
  contribution: { type: 'aggregate' | 'detail' }
}
type V2Progress = {
  v: 2
  rootId: string
  nodes: V2Node[]
  projections: Record<string, Projection>
}

const SETUP_STAGE_LABEL: Record<SetupStage, string> = {
  provisioning: 'Szukanie GPU',
  blender_download: 'Pobieranie Blendera',
  blender_extract: 'Rozpakowywanie Blendera',
  blender_deps: 'Zależności Blendera',
  blender_ready: 'Blender gotowy',
  scene_download: 'Pobieranie sceny',
  addon_download: 'Pobieranie addonu',
  addon_extract: 'Instalacja addonu',
  addon_ready: 'Addon gotowy',
  render_start: 'Renderowanie',
}

const STATUS_LABEL: Record<JobStatus, string> = {
  preparing: 'Preparing',
  queued: 'Queued',
  provisioning: 'Provisioning',
  rendering: 'Rendering',
  uploading: 'Uploading',
  done: 'Done',
  delivering: 'Delivering',
  delivered: 'Delivered',
  delivery_failed: 'Delivery Failed',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

function isV2(
  progress: Job['progress'],
): progress is Job['progress'] & V2Progress {
  return (
    progress !== null &&
    progress !== undefined &&
    progress.v === 2 &&
    'nodes' in progress
  )
}

function v1Entries(progress: Job['progress']): V1Entry[] {
  if (!progress || isV2(progress) || !('entries' in progress)) return []
  return (progress as V1Progress).entries
}

function overallPercent(entries: V1Entry[]): number {
  const total = entries.reduce((sum, entry) => sum + entry.total, 0)
  if (total === 0) return 0
  const current = entries.reduce(
    (sum, entry) => sum + Math.min(entry.current, entry.total),
    0,
  )
  return Math.round((current / total) * 100)
}

function activePath(progress: V2Progress): string | null {
  const byId = new Map(progress.nodes.map((node) => [node.id, node]))
  const active = [...progress.nodes]
    .reverse()
    .find((node) => node.state === 'running' && node.id !== progress.rootId)
  if (!active) return null
  const labels: string[] = []
  let current: V2Node | undefined = active
  while (current && current.id !== progress.rootId) {
    labels.unshift(current.activity ?? current.label)
    current = current.parentId ? byId.get(current.parentId) : undefined
  }
  return labels.join(' → ')
}

function formatProjection(projection: Projection | undefined): string {
  if (
    !projection ||
    projection.completed === null ||
    projection.completed === undefined
  )
    return '—'
  const total =
    projection.total === null || projection.total === undefined
      ? '?'
      : projection.total
  return `${projection.completed}/${total}${projection.unit ? ` ${projection.unit}` : ''}`
}

function ProgressTree({
  progress,
  parentId,
  depth = 0,
}: {
  progress: V2Progress
  parentId: string
  depth?: number
}) {
  const children = progress.nodes
    .filter((node) => node.parentId === parentId)
    .sort((a, b) => a.order - b.order)
  if (children.length === 0) return null
  return (
    <ul className={depth === 0 ? 'mt-3 space-y-1' : 'ml-4 mt-1 space-y-1'}>
      {children.map((node) => {
        const nested = progress.nodes.some(
          (candidate) => candidate.parentId === node.id,
        )
        const row = (
          <div className="flex justify-between gap-3 text-xs text-muted-foreground tabular-nums">
            <span>
              {node.label}
              {node.activity ? ` — ${node.activity}` : ''}
            </span>
            <span>{formatProjection(progress.projections[node.id])}</span>
          </div>
        )
        return (
          <li key={node.id} data-progress-node={node.id}>
            {nested ? (
              <details open={node.state === 'running'}>
                <summary className="cursor-pointer list-none">{row}</summary>
                <ProgressTree
                  progress={progress}
                  parentId={node.id}
                  depth={depth + 1}
                />
              </details>
            ) : (
              row
            )}
          </li>
        )
      })}
    </ul>
  )
}

export function PipelineProgress({
  status,
  progress,
  setupStage,
  setupPercent,
  progressPercent,
}: {
  status: JobStatus
  progress: Job['progress']
  setupStage: SetupStage | null
  setupPercent: number | null
  progressPercent: number
}) {
  const v2 = isV2(progress) ? (progress as unknown as V2Progress) : null
  const entries = v1Entries(progress)
  const rootProjection = v2?.projections[v2.rootId]
  const rootFraction = rootProjection?.fractionMillionths

  let fillPct = 0
  let isIndeterminate = false
  let label = STATUS_LABEL[status]
  let estimated = false

  if (v2) {
    isIndeterminate = rootFraction === null || rootFraction === undefined
    fillPct =
      rootFraction === null || rootFraction === undefined
        ? 0
        : Math.round(rootFraction / 10_000)
    estimated = rootProjection?.accuracy === 'estimated'
    label = activePath(v2) ?? label
  } else if (entries.length > 0) {
    fillPct = overallPercent(entries)
  } else if (setupStage) {
    fillPct = setupPercent ?? 0
    isIndeterminate = setupPercent === null
    label = SETUP_STAGE_LABEL[setupStage]
  } else if (status === 'rendering' || status === 'uploading') {
    fillPct = progressPercent
  } else {
    isIndeterminate = true
  }

  const detail = isIndeterminate
    ? label
    : `${label} — ${estimated ? '~' : ''}${fillPct}%`
  return (
    <div
      className="bg-card rounded-lg border border-border p-4"
      data-testid="pipeline-progress"
    >
      <p
        className="text-sm font-medium text-foreground mb-3"
        data-testid="progress-label"
      >
        {detail}
      </p>
      <div className="h-3 rounded-full overflow-hidden bg-muted">
        {isIndeterminate ? (
          <div className="h-full bg-info animate-pulse" />
        ) : (
          <div
            className="h-full bg-success transition-all duration-500"
            style={{ width: `${fillPct}%` }}
          />
        )}
      </div>
      {v2 ? (
        <ProgressTree progress={v2} parentId={v2.rootId} />
      ) : entries.length > 1 ? (
        <ul className="mt-3 space-y-1" data-testid="progress-entries">
          {entries.map((entry) => (
            <li
              key={entry.key}
              className="flex justify-between text-xs text-muted-foreground tabular-nums"
            >
              <span>{entry.label ?? entry.key}</span>
              <span>
                {Math.min(entry.current, entry.total)}/{entry.total}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
